import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import FileDropzone from "@/components/FileDropzone";
import ServerStlPreview from "@/components/ServerStlPreview";
import { StatusBadge, STATUS_META, formatDate, formatBytes, isStlFile, isHtmlFile, fileRowClasses, FileTrackTag, FileTrackLegend, OrderProgress, DeadlineCell } from "@/lib/orderUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { ORDER_OPTIONS_DEFAULTS } from "@/lib/orderOptionsDefaults";
import { deepMerge } from "@/lib/siteDefaults";
import { format } from "date-fns";
import { isNonWorkingDay, addWorkingDays, orderMinWorkingDays, upcomingClosuresLabel } from "@/lib/workingDays";
import { uploadOrderFile } from "@/lib/upload";
import UploadProgress from "@/components/UploadProgress";
import { it } from "date-fns/locale";
import { ArrowLeft, Loader2, Download, FileText, FileSignature, PackageCheck, User, Building2, Mail, Upload, Trash2, Box, Pencil, CalendarIcon, Plus, X, Clock3, Zap, Globe } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [orderDeleteOpen, setOrderDeleteOpen] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "admin" || user?.role === "operator";
  const [options, setOptions] = useState(ORDER_OPTIONS_DEFAULTS);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editDeadline, setEditDeadline] = useState();
  const [savingEdit, setSavingEdit] = useState(false);
  const [updAttach, setUpdAttach] = useState([]);
  const [updPrescr, setUpdPrescr] = useState([]);
  const [updNote, setUpdNote] = useState("");
  const [updUrgency, setUpdUrgency] = useState(false);
  const [missingFiles, setMissingFiles] = useState([]);
  const [missingUploading, setMissingUploading] = useState(false);

  const submitMissingFiles = async () => {
    setMissingUploading(true);
    try {
      for (const f of missingFiles) {
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        const kind = ["stl", "dcm", "zip", "ply", "obj"].includes(ext) ? "allegato" : "prescrizione";
        await uploadOrderFile(id, f, { kind, onProgress: (p) => setUploadProgress({ pct: Math.round(p * 100), name: f.name }) });
      }
      toast.success("File caricati");
      setMissingFiles([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento non riuscito. Riprova.");
      load();
    } finally {
      setMissingUploading(false);
      setUploadProgress(null);
    }
  };
  const [uploadProgress, setUploadProgress] = useState(null);
  const [updDeadline, setUpdDeadline] = useState(undefined);
  const [updSubmitting, setUpdSubmitting] = useState(false);
  const [ackLoading, setAckLoading] = useState(false);
  const [ackLabLoading, setAckLabLoading] = useState(false);

  const ackLabUpdate = async () => {
    setAckLabLoading(true);
    try {
      await api.post(`/orders/${id}/ack-lab-update`);
      load();
    } catch (e) {
      toast.error("Errore");
    } finally {
      setAckLabLoading(false);
    }
  };
  const [sharing, setSharing] = useState(false);

  const toggleShare = async () => {
    setSharing(true);
    try {
      await api.patch(`/orders/${id}/share`, { shared: !order.shared_with_operator });
      toast.success(!order.shared_with_operator ? "Caso condiviso con il collega" : "Condivisione rimossa");
      load();
    } catch (e) {
      toast.error("Errore nell'aggiornare la condivisione");
    } finally {
      setSharing(false);
    }
  };

  const [billingBusy, setBillingBusy] = useState(false);
  const setBilling = async (action) => {
    setBillingBusy(true);
    try {
      await api.patch(`/orders/${id}/billing-month`, { action });
      toast.success(action === "reset" ? "Spostamento annullato" : "Ordine spostato al mese successivo");
      load();
    } catch (e) {
      toast.error("Errore nell'aggiornare il mese contabile");
    } finally {
      setBillingBusy(false);
    }
  };
  const MESI_IT = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
  const meseLabel = (ym) => (ym ? `${MESI_IT[parseInt(ym.slice(5, 7), 10) - 1]} ${ym.slice(0, 4)}` : "");

  const [editItems, setEditItems] = useState([{ type: "", quantity: "" }]);
  const lavorazioniOptions = [...new Set([...(options.materials || []), ...(options.design_types || [])])];
  const updMinDays = orderMinWorkingDays(order, options, user?.client_type || "laboratorio");
  const updMinDate = addWorkingDays(new Date(), updMinDays, options.closure_days || []);
  const editTotal = editItems.reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
  const addEditItem = () => setEditItems([...editItems, { type: "", quantity: "" }]);
  const removeEditItem = (i) => setEditItems(editItems.filter((_, idx) => idx !== i));
  const updateEditItem = (i, key, val) => setEditItems(editItems.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));

  const openEdit = () => {
    setEditForm({
      patient_name: order.patient_name || "",
      macro_category: order.macro_category || "",
      color: order.color || "",
      notes: order.notes || "",
    });
    const its = (order.items && order.items.length)
      ? order.items.map((it) => ({ type: it.type || "", quantity: String(it.quantity || "") }))
      : [{ type: order.material || order.design_type || "", quantity: order.quantity ? String(order.quantity) : "" }];
    setEditItems(its.length ? its : [{ type: "", quantity: "" }]);
    setEditDeadline(order.deadline ? new Date(order.deadline) : undefined);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.patient_name || !editForm.macro_category) {
      toast.error("Compila Paziente e Tipo di lavoro");
      return;
    }
    const validItems = editItems.filter((it) => it.type && Number(it.quantity) > 0);
    if (validItems.length === 0) {
      toast.error("Aggiungi almeno una lavorazione con la relativa quantità");
      return;
    }
    setSavingEdit(true);
    try {
      const totalEl = validItems.reduce((s, it) => s + Number(it.quantity), 0);
      const payload = {
        patient_name: editForm.patient_name,
        macro_category: editForm.macro_category,
        color: editForm.color || null,
        material: null,
        design_type: null,
        items: validItems.map((it) => ({ type: it.type, quantity: Number(it.quantity) })),
        quantity: String(totalEl),
        work_type: validItems.map((it) => `${it.type} ×${it.quantity}`).join(", "),
        notes: editForm.notes || "",
        deadline: editDeadline ? editDeadline.toISOString() : null,
      };
      await api.patch(`/orders/${id}`, payload);
      toast.success("Ordine aggiornato");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error("Errore nel salvataggio delle modifiche");
    } finally {
      setSavingEdit(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      setAdminNote(data.admin_note || "");
    } catch (e) {
      toast.error("Ordine non trovato");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    api.get("/order-options")
      .then(({ data }) => { if (data && Object.keys(data).length) setOptions(deepMerge(ORDER_OPTIONS_DEFAULTS, data)); })
      .catch(() => {});
  }, []);

  const uploadMore = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        await uploadOrderFile(id, f, { kind: isStaff ? "lavorato" : "allegato", onProgress: (p) => setUploadProgress({ pct: Math.round(p * 100), name: f.name }) });
      }
      toast.success("File caricati");
      setFiles([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento file. Riprova.");
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const [projectFiles, setProjectFiles] = useState([]);
  const [projectUploading, setProjectUploading] = useState(false);
  const uploadProjects = async () => {
    if (projectFiles.length === 0) return;
    setProjectUploading(true);
    try {
      for (const f of projectFiles) {
        await uploadOrderFile(id, f, { kind: "progetto", onProgress: (p) => setUploadProgress({ pct: Math.round(p * 100), name: f.name }) });
      }
      toast.success("Progetti CAD caricati");
      setProjectFiles([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento dei progetti. Riprova.");
    } finally {
      setProjectUploading(false);
      setUploadProgress(null);
    }
  };

  const submitClientUpdate = async () => {
    if (updAttach.length === 0 && updPrescr.length === 0) {
      toast.error("Aggiungi almeno un file STL/ZIP o una prescrizione");
      return;
    }
    if (!updDeadline) {
      toast.error("Inserisci la nuova data di scadenza (obbligatoria)");
      return;
    }
    setUpdSubmitting(true);
    try {
      const upload = async (list, kind) => {
        for (const f of list) {
          await uploadOrderFile(id, f, { kind, isUpdate: true, onProgress: (p) => setUploadProgress({ pct: Math.round(p * 100), name: f.name }) });
        }
      };
      await upload(updAttach, "allegato");
      await upload(updPrescr, "prescrizione");
      try {
        await api.post(`/orders/${id}/client-update`, { note: updNote, deadline: updDeadline.toISOString(), urgency_requested: updUrgency && options.urgency_enabled !== false });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "File caricati, ma l'invio della nota/scadenza non è riuscito. Riprova.");
        load();
        return;
      }
      toast.success("Aggiornamento inviato al laboratorio");
      setUpdAttach([]); setUpdPrescr([]); setUpdNote(""); setUpdDeadline(undefined);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento dei file. Controlla la connessione e riprova.");
      load();
    } finally {
      setUpdSubmitting(false);
      setUploadProgress(null);
    }
  };

  const ackUpdate = async () => {
    setAckLoading(true);
    try {
      await api.post(`/orders/${id}/ack-update`);
      toast.success("Aggiornamento segnato come visto");
      load();
    } catch (e) {
      toast.error("Errore");
    } finally {
      setAckLoading(false);
    }
  };

  const buildDownloadName = (ext) => {
    const d = new Date();
    const date = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    const clean = (s) => (s || "").replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_");
    const patient = clean(order?.patient_name) || "paziente";
    const client = clean(order?.client_company || order?.client_name) || "cliente";
    return `${date}_${patient}_${client}${ext ? "." + ext : ""}`;
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
    } catch (e) {
      const st = e.response?.status;
      const msg = st === 401 ? "Sessione scaduta: esci e accedi di nuovo" : st === 403 ? "Accesso negato a questo file" : st === 404 ? "File non trovato" : st ? `Errore nel download (codice ${st})` : "Errore nel download: connessione interrotta o bloccata dal browser/antivirus";
      toast.error(msg, {
        description: "Puoi provare ad aprirlo direttamente in una nuova scheda.",
        action: { label: "Apri", onClick: () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download`, "_blank") },
        duration: 8000,
      });
    }
  };

  const [zipping, setZipping] = useState(false);
  const downloadZip = async (updateDate) => {
    setZipping(true);
    try {
      const q = updateDate ? { check: 1, update_date: updateDate } : { check: 1 };
      await api.get(`/orders/${id}/download-zip`, { params: q });
      const zipName = updateDate ? buildDownloadName("zip").replace(/\.zip$/i, `_aggiornamento_${updateDate}.zip`) : buildDownloadName("zip");
      // Download diretto dal browser (streaming su disco): niente limiti di memoria/timeout del blob
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/download-zip?name=${encodeURIComponent(zipName)}${updateDate ? `&update_date=${updateDate}` : ""}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = zipName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 1500);
      toast.success("Download ZIP avviato: controlla la barra download del browser");
    } catch (e) {
      const st = e.response?.status;
      toast.error(st === 404 ? "Nessun file da scaricare" : st === 401 ? "Sessione scaduta: esci e accedi di nuovo" : st ? `Errore nel download ZIP (codice ${st})` : "Errore di connessione nel download ZIP");
    } finally {
      setZipping(false);
    }
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/files/${fileToDelete.id}`);
      toast.success("File eliminato");
      setFileToDelete(null);
      load();
    } catch (e) {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteOrder = async () => {
    setDeletingOrder(true);
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Ordine eliminato");
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error("Errore durante l'eliminazione dell'ordine");
      setDeletingOrder(false);
    }
  };

  const updateStatus = async (status) => {    try {
      await api.patch(`/orders/${id}/status`, { status, admin_note: adminNote });
      toast.success("Ordine aggiornato");
      load();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  if (loading || !order) {
    return (
      <DashboardLayout title="Dettaglio ordine">
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
      </DashboardLayout>
    );
  }

  const prescriptions = (order.files || []).filter((f) => f.kind === "prescrizione");
  const finished = (order.files || []).filter((f) => f.kind === "lavorato");
  const projects = (order.files || []).filter((f) => f.kind === "progetto");
  const attachments = (order.files || []).filter((f) => f.kind !== "prescrizione" && f.kind !== "lavorato" && f.kind !== "progetto");
  const updateGroups = Object.entries(attachments.filter((f) => f.is_update).reduce((acc, f) => { const k = (f.created_at || "").slice(0, 10) || "senza-data"; (acc[k] ||= []).push(f); return acc; }, {})).sort((a, b) => b[0].localeCompare(a[0]));

  const fmtDateTime = (iso) => {
    if (!iso) return null;
    try { return format(new Date(iso), "dd MMM yyyy, HH:mm", { locale: it }); } catch { return null; }
  };

  const FileRow = ({ f, accent }) => (
    <li className={`flex items-center justify-between rounded-lg border px-3 py-2.5 transition-colors ${fileRowClasses(f)}`}>
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex items-center gap-2.5 min-w-0">
          {accent ? <FileSignature className="h-4 w-4 text-teal-600 shrink-0" /> : isStlFile(f.filename) ? <Box className="h-4 w-4 text-teal-600 shrink-0" /> : <FileText className="h-4 w-4 text-teal-600 shrink-0" />}
          <span className="text-sm text-slate-700 truncate">{f.filename}</span>
          <span className="text-xs text-slate-400 shrink-0">{formatBytes(f.size)}</span>
        </div>
        <div className="flex items-center flex-wrap gap-2 pl-6">
          {f.is_update && (
            <span data-testid="file-update-badge" className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold px-2 py-0.5">
              <Clock3 className="h-3 w-3" /> Aggiornamento
            </span>
          )}
          {f.created_at && (
            <span data-testid="file-date" className="text-xs text-slate-400">Caricato il {fmtDateTime(f.created_at)}</span>
          )}
        </div>
        <FileTrackTag file={f} />
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isStlFile(f.filename) && <ServerStlPreview fileId={f.id} filename={f.filename} />}
        {isHtmlFile(f.filename) && (
          <Button size="sm" variant="outline" data-testid="open-html-project-button" className="border-teal-300 text-teal-700 hover:bg-teal-50"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download`, "_blank", "noopener")}>
            <Globe className="h-4 w-4 mr-1" /> Apri progetto
          </Button>
        )}
        <Button size="sm" variant="ghost" data-testid="download-file-button" onClick={() => downloadFile(f.id, f.filename)}>
          <Download className="h-4 w-4" />
        </Button>
        {isAdmin && (
          <Button size="sm" variant="ghost" data-testid="delete-file-button" onClick={() => setFileToDelete(f)} className="text-slate-400 hover:text-red-600 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </li>
  );

  return (
    <DashboardLayout
      title={order.patient_name}
      subtitle={`${order.order_number} · ${order.macro_category || order.work_type || "—"}`}
      actions={
        <div className="flex items-center gap-2">
          {isStaff && (
            <Button data-testid="download-zip-button" onClick={() => downloadZip()} disabled={zipping} className="bg-teal-600 hover:bg-teal-700 text-white shadow-sm">
              {zipping ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Scarica tutto (ZIP)
            </Button>
          )}
          <Button data-testid="back-button" onClick={() => navigate(-1)} className="bg-white text-slate-800 hover:bg-slate-100 border border-slate-300 shadow-sm"><ArrowLeft className="h-4 w-4 mr-1.5" /> Indietro</Button>
        </div>
      }
    >
      {isAdmin && (
        <div data-testid="share-bar" className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="min-w-0">
            <p className="font-semibold text-slate-800">Condivisione con il collega</p>
            <p className="text-sm text-slate-500">{order.shared_with_operator ? "Il collega può vedere e lavorare questo caso." : "Il collega NON vede questo caso."}</p>
          </div>
          <Button data-testid="share-toggle-button" onClick={toggleShare} disabled={sharing} variant={order.shared_with_operator ? "outline" : "default"} className={`shrink-0 ${order.shared_with_operator ? "border-teal-300 text-teal-700" : "bg-teal-600 hover:bg-teal-700"}`}>
            {sharing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}{order.shared_with_operator ? "Rimuovi condivisione" : "Condividi con collega"}
          </Button>
        </div>
      )}
      {isAdmin && order.status === "completato" && (
        <div data-testid="billing-bar" className={`mb-6 flex items-center justify-between gap-4 rounded-xl border p-4 ${order.billing_month ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
          <div className="min-w-0">
            <p className="font-semibold text-slate-800">Mese contabile (report clienti)</p>
            <p className="text-sm text-slate-500" data-testid="billing-month-text">
              {order.billing_month
                ? <>Sospeso: conteggiato nel report di <span className="font-semibold text-amber-700">{meseLabel(order.billing_month)}</span> (completato a {meseLabel((order.completed_at || order.updated_at || order.created_at || "").slice(0, 7))}).</>
                : <>Conteggiato nel mese di completamento ({meseLabel((order.completed_at || order.updated_at || order.created_at || "").slice(0, 7))}).</>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {order.billing_month && (
              <Button data-testid="billing-reset-button" variant="outline" size="sm" onClick={() => setBilling("reset")} disabled={billingBusy}>Annulla</Button>
            )}
            <Button data-testid="billing-next-button" size="sm" onClick={() => setBilling("next")} disabled={billingBusy} className="bg-amber-600 hover:bg-amber-700">
              {billingBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CalendarIcon className="h-4 w-4 mr-1.5" />} Sposta al mese successivo
            </Button>
          </div>
        </div>
      )}
      {order.urgency_requested && (
        <div data-testid="urgent-banner" className="mb-6 flex items-center gap-3 rounded-xl border-2 border-red-500 bg-red-600 p-4 text-white shadow-md">
          <Zap className="h-6 w-6 shrink-0 animate-pulse" />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold uppercase tracking-wide">Caso urgente</p>
            <p className="text-sm text-red-50">Il cliente ha richiesto l'urgenza per questo lavoro{order.deadline ? ` · scadenza richiesta: ${formatDate(order.deadline)}` : ""}.</p>
          </div>
        </div>
      )}
      {isStaff && order.has_client_update && (
        <div data-testid="client-update-banner" className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-red-600 text-white text-lg font-bold shrink-0">!</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-red-800">Aggiornamento post-prova dal cliente</p>
            <p className="text-sm text-red-700">Il cliente ha caricato nuovi file/prescrizione{order.client_update_at ? ` il ${formatDate(order.client_update_at)}` : ""}.{order.client_update_note ? ` Nota: "${order.client_update_note}"` : ""}</p>
          </div>
          <Button size="sm" data-testid="ack-update-button" onClick={ackUpdate} disabled={ackLoading} className="bg-red-600 hover:bg-red-700 shrink-0">
            {ackLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Segna come visto
          </Button>
        </div>
      )}
      {isStaff && (order.client_updates?.length > 0) && (
        <div data-testid="client-updates-history" className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <p className="font-semibold text-amber-800 flex items-center gap-2 mb-3"><Clock3 className="h-4 w-4" /> Storico aggiornamenti del cliente ({order.client_updates.length})</p>
          <ol className="space-y-2">
            {[...order.client_updates].reverse().map((u, i) => (
              <li key={i} data-testid="client-update-history-item" className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 rounded-lg bg-white border border-amber-100 px-3 py-2">
                <span className="text-xs font-semibold text-amber-700 shrink-0 sm:w-40">{fmtDateTime(u.at)}</span>
                <span className="text-sm text-slate-700 flex-1">{u.note ? u.note : <span className="italic text-slate-400">Nessuna nota</span>}{u.deadline ? <span className="block text-xs text-slate-400 mt-0.5">Nuova scadenza richiesta: {formatDate(u.deadline)}</span> : null}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
      {(() => {
        const hist = order.deadline_history?.length ? order.deadline_history : (order.client_updates || []).filter((u) => u.deadline).map((u) => ({ at: u.at, to: u.deadline, by_name: order.client_name, by_role: "client" }));
        if (!hist.length) return null;
        const roleLabel = { client: "Cliente", admin: "Laboratorio", operator: "Collega" };
        return (
          <div data-testid="deadline-history" className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
            <p className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><CalendarIcon className="h-4 w-4 text-teal-600" /> Storico scadenze ({hist.length})</p>
            <ol className="space-y-2">
              {[...hist].reverse().map((h, i) => (
                <li key={i} data-testid="deadline-history-item" className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-sm">
                  <span className="text-xs font-semibold text-slate-500 shrink-0 sm:w-40">{fmtDateTime(h.at)}</span>
                  <span className="flex-1 text-slate-700">
                    {h.from ? <><span className="line-through text-slate-400">{formatDate(h.from)}</span> <span className="mx-1">→</span></> : null}
                    <span className="font-semibold text-slate-900">{formatDate(h.to)}</span>
                  </span>
                  <span className="text-xs text-slate-500 shrink-0">{roleLabel[h.by_role] || h.by_role}{h.by_name && h.by_name !== (roleLabel[h.by_role] || "") ? ` · ${h.by_name}` : ""}</span>
                </li>
              ))}
            </ol>
          </div>
        );
      })()}
      {order.status === "in_verifica_cliente" && (
        <div data-testid="verification-banner" className="mb-6 flex items-start gap-3 rounded-xl border-2 border-orange-400 bg-orange-50 p-4">
          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-orange-600 text-white text-lg font-bold shrink-0">!</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-orange-900 uppercase tracking-wide">In verifica per cliente</p>
            <p className="text-sm text-orange-800">
              {isStaff
                ? <>Caso in attesa del riscontro del cliente{order.verification_requested_at ? ` dal ${formatDate(order.verification_requested_at)}` : ""}{order.verification_requested_by ? ` (impostato da ${order.verification_requested_by})` : ""}. Il cliente ha ricevuto un'email di avviso.</>
                : <>Il laboratorio ha bisogno della tua verifica{order.verification_requested_at ? ` (richiesta il ${formatDate(order.verification_requested_at)})` : ""}: controlla i file e le indicazioni e invia il tuo riscontro dal pulsante "Invia aggiornamento" qui sotto.</>}
            </p>
          </div>
        </div>
      )}
      {!isStaff && order.has_lab_update && (
        <div data-testid="lab-update-banner" className="mb-6 flex items-start gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4">
          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-indigo-600 text-white text-lg font-bold shrink-0">!</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-indigo-800">Aggiornamento dal laboratorio</p>
            <p className="text-sm text-indigo-700">Il laboratorio ha caricato un aggiornamento{order.lab_update_at ? ` il ${formatDate(order.lab_update_at)}` : ""}. Controlla i file allegati qui sotto.</p>
          </div>
          <Button size="sm" variant="outline" data-testid="ack-lab-update-button" onClick={ackLabUpdate} disabled={ackLabLoading} className="border-indigo-300 text-indigo-700 hover:bg-indigo-100 shrink-0">
            {ackLabLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Ho visto
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200" data-testid="order-progress-card">
            <h3 className="font-heading text-lg font-semibold mb-5">Stato di lavorazione</h3>
            <OrderProgress order={order} />
          </Card>
          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold">Dettagli lavoro</h3>
              <div className="flex items-center gap-2">
                {isStaff && (
                  <Button size="sm" variant="outline" data-testid="edit-order-button" onClick={openEdit} className="border-teal-300 text-teal-700 hover:bg-teal-50">
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Modifica
                  </Button>
                )}
                <StatusBadge status={order.status} />
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <Detail label="Tipo di lavoro" value={order.macro_category || "—"} />
              <Detail label="Quantità totale" value={order.quantity || "—"} />
              <Detail label="Colore" value={order.color || "—"} />
              <Detail label="Scadenza" value={<DeadlineCell order={order} />} />
            </dl>
            {(order.items && order.items.length > 0) ? (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Lavorazioni</p>
                <ul className="space-y-1.5" data-testid="order-items-list">
                  {order.items.map((it, idx) => (
                    <li key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{it.type}</span>
                      <span className="font-semibold text-slate-800">×{it.quantity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (order.material || order.design_type) ? (
              <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-2 gap-4 text-sm">
                {order.material && <Detail label="Materiale" value={order.material} />}
                {order.design_type && <Detail label="Tipo di progettazione" value={order.design_type} />}
              </div>
            ) : null}
            {order.notes && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Note del cliente</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.notes}</p>
              </div>
            )}
            {order.admin_note && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <p className="text-xs font-bold uppercase tracking-wider text-teal-600 mb-1">Nota del laboratorio</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.admin_note}</p>
              </div>
            )}
          </Card>

          {isStaff && (
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-order-dialog">
                <DialogHeader>
                  <DialogTitle>Modifica ordine {order.order_number}</DialogTitle>
                  <DialogDescription>Correggi i dati inseriti dal cliente: lavorazioni, elementi, scadenza e note.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nome paziente / caso</Label>
                    <Input data-testid="edit-patient-input" value={editForm.patient_name || ""} onChange={(e) => setEditForm({ ...editForm, patient_name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo di lavoro</Label>
                    <Select value={editForm.macro_category} onValueChange={(v) => setEditForm({ ...editForm, macro_category: v })}>
                      <SelectTrigger data-testid="edit-macro-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {(options.macro_categories || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {!!editForm.macro_category && (
                    <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                      <Label>Lavorazioni e quantità</Label>
                      <div className="space-y-2">
                        {editItems.map((it, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Select value={it.type} onValueChange={(v) => updateEditItem(i, "type", v)}>
                              <SelectTrigger data-testid={`edit-item-type-${i}`} className="flex-1"><SelectValue placeholder="Tipo di lavorazione" /></SelectTrigger>
                              <SelectContent className="max-h-60">
                                {lavorazioniOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Input type="number" min={1} data-testid={`edit-item-qty-${i}`} value={it.quantity} onChange={(e) => updateEditItem(i, "quantity", e.target.value)} placeholder="Q.tà" className="w-24" />
                            <Button type="button" variant="ghost" size="icon" data-testid={`edit-item-remove-${i}`} onClick={() => removeEditItem(i)} disabled={editItems.length === 1} className="text-slate-400 hover:text-red-600 shrink-0"><X className="h-4 w-4" /></Button>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <Button type="button" variant="outline" size="sm" data-testid="edit-add-item-button" onClick={addEditItem}><Plus className="h-4 w-4 mr-1.5" /> Aggiungi lavorazione</Button>
                        <span className="text-xs font-medium text-slate-500">Totale elementi: {editTotal}</span>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Colore</Label>
                      <Select value={editForm.color} onValueChange={(v) => setEditForm({ ...editForm, color: v })}>
                        <SelectTrigger data-testid="edit-color-select"><SelectValue placeholder="Scala VITA" /></SelectTrigger>
                        <SelectContent className="max-h-60">
                          {(options.colors || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Scadenza</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" data-testid="edit-deadline-trigger" className="w-full justify-start font-normal">
                            <CalendarIcon className="h-4 w-4 mr-2 text-slate-400" />
                            {editDeadline ? format(editDeadline, "dd MMM yy", { locale: it }) : "Data"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={editDeadline} onSelect={setEditDeadline} disabled={user?.role === "admin" ? undefined : (d) => isNonWorkingDay(d, options.closure_days || [])} initialFocus />
                        </PopoverContent>
                      </Popover>
                      {user?.role === "admin" && <p className="text-[11px] text-slate-400" data-testid="admin-deadline-hint">Come amministratore puoi scegliere anche weekend, festività e giorni di chiusura.</p>}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Note</Label>
                    <Textarea data-testid="edit-notes-input" value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
                  <Button data-testid="save-edit-order-button" onClick={saveEdit} disabled={savingEdit} className="bg-teal-600 hover:bg-teal-700">
                    {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Salva modifiche
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {prescriptions.length > 0 && (
            <Card className="p-6 border-teal-200 bg-teal-50/40">
              <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><FileSignature className="h-5 w-5 text-teal-600" /> Prescrizione</h3>
              <ul className="space-y-2 bg-white rounded-lg p-1" data-testid="order-prescriptions-list">
                {prescriptions.map((f) => <FileRow key={f.id} f={f} accent />)}
              </ul>
            </Card>
          )}

          {finished.length > 0 && (
            <Card className="p-6 border-emerald-200 bg-emerald-50/40">
              <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><PackageCheck className="h-5 w-5 text-emerald-600" /> File lavorati</h3>
              <ul className="space-y-2 bg-white rounded-lg p-1" data-testid="order-finished-list">
                {finished.map((f) => <FileRow key={f.id} f={f} />)}
              </ul>
            </Card>
          )}
          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
              <h3 className="font-heading text-lg font-semibold">File allegati ({attachments.length})</h3>
              {isStaff && <FileTrackLegend />}
            </div>
            {attachments.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Nessun file allegato.</p>
            ) : (
              <>
                <ul className="space-y-2" data-testid="order-files-list">
                  {attachments.filter((f) => !f.is_update).map((f) => <FileRow key={f.id} f={f} />)}
                </ul>
                {updateGroups.map(([day, list]) => (
                  <div key={day} data-testid="update-day-group" className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-amber-800 flex items-center gap-2"><Clock3 className="h-4 w-4" /> Aggiornamento del {format(new Date(day + "T00:00:00"), "dd/MM/yyyy")} · {list.length} file</p>
                      {isStaff && (
                        <Button size="sm" variant="outline" data-testid="download-update-zip-button" disabled={zipping} onClick={() => downloadZip(day)} className="border-amber-300 text-amber-800 hover:bg-amber-100">
                          <PackageCheck className="h-4 w-4 mr-1" /> ZIP di questo aggiornamento
                        </Button>
                      )}
                    </div>
                    <ul className="space-y-2">{list.map((f) => <FileRow key={f.id} f={f} />)}</ul>
                  </div>
                ))}
              </>
            )}
            {isStaff && (
              <div data-testid="lab-upload-zone" className="mt-5 rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 space-y-3">
                <Label className="text-xs font-bold uppercase tracking-wider text-indigo-700">Carica file lavorati (di ritorno)</Label>
                <FileDropzone files={files} setFiles={setFiles} disabled={uploading} hint="STL, ZIP, progetti HTML, XML, immagini, PDF o cartelle non compresse · max 100MB per file" />
                {files.length > 0 && (
                  <Button data-testid="upload-more-button" onClick={uploadMore} disabled={uploading} className="bg-indigo-600 hover:bg-indigo-700">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Carica {files.length} file
                  </Button>
                )}
                <UploadProgress progress={uploadProgress} />
              </div>
            )}
          </Card>

          {isStaff && (
            <Card className="p-6 border-violet-200 bg-violet-50/70" data-testid="cad-projects-card">
              <div className="flex items-start justify-between gap-3 mb-1">
                <h3 className="font-heading text-lg font-semibold flex items-center gap-2 text-violet-900"><Box className="h-5 w-5" /> Progetti CAD · interno laboratorio ({projects.length})</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-violet-200 text-violet-900 px-2 py-0.5 shrink-0">Non visibile al cliente</span>
              </div>
              <p className="text-sm text-slate-600 mb-4">File di lavoro del paziente (.dentalProject, .dentalCAD, STL, ZIP…) condivisi tra te e i collaboratori: chi deve fare modifiche li scarica da qui.</p>
              {projects.length > 0 && (
                <ul className="space-y-2 mb-4" data-testid="cad-projects-list">
                  {[...projects].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).map((f) => <FileRow key={f.id} f={f} />)}
                </ul>
              )}
              <div className="rounded-lg border border-violet-200 bg-white p-4 space-y-3">
                <Label className="text-xs font-bold uppercase tracking-wider text-violet-700">Carica progetti CAD</Label>
                <FileDropzone files={projectFiles} setFiles={setProjectFiles} disabled={projectUploading} hint=".dentalProject, .dentalCAD, STL, ZIP o cartelle · file pesanti inviati a blocchi · max 500MB per file" />
                {projectFiles.length > 0 && (
                  <Button data-testid="upload-projects-button" onClick={uploadProjects} disabled={projectUploading} className="bg-violet-600 hover:bg-violet-700">
                    {projectUploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Carica {projectFiles.length} progetto/i
                  </Button>
                )}
                <UploadProgress progress={uploadProgress} />
              </div>
            </Card>
          )}

          {!isStaff && (order.files || []).length === 0 && order.status === "ricevuto" && (
            <Card className="p-6 border-red-200 bg-red-50" data-testid="client-missing-files-card">
              <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2 text-red-800"><Upload className="h-5 w-5" /> File del caso mancanti</h3>
              <p className="text-sm text-slate-600 mb-4">Questo ordine è stato creato ma non contiene ancora nessun file. Carica qui STL/ZIP e prescrizione: non è necessario ricreare il caso.</p>
              <div className="space-y-3">
                <FileDropzone files={missingFiles} setFiles={setMissingFiles} disabled={missingUploading} maxFiles={12} hint="STL, DCM, ZIP, PDF, immagini o cartelle non compresse · max 100MB per file" />
                <Button data-testid="submit-missing-files" onClick={submitMissingFiles} disabled={missingUploading || missingFiles.length === 0} className="bg-red-600 hover:bg-red-700">
                  {missingUploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Carica file
                </Button>
                <UploadProgress progress={uploadProgress} />
              </div>
            </Card>
          )}

          {!isStaff && (
            <Card className="p-6 border-amber-200 bg-amber-50" data-testid="client-update-card">
              <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2"><Upload className="h-5 w-5 text-amber-600" /> Aggiornamento post-prova</h3>
              <p className="text-sm text-slate-600 mb-4">Hai modifiche dopo la prova? Ricarica qui STL/ZIP e/o una nuova prescrizione per questo stesso caso. Il laboratorio riceverà una segnalazione.</p>
              <div className="space-y-4">
                <div className="space-y-2 rounded-lg border border-teal-200 bg-white p-3">
                  <Label className="flex items-center gap-1.5 text-teal-800"><Box className="h-4 w-4" /> Nuovo STL / Progetto 3D <span className="text-xs font-normal text-teal-600">(STL, DCM, ZIP)</span></Label>
                  <FileDropzone files={updAttach} setFiles={setUpdAttach} disabled={updSubmitting} allowedExts={["stl", "dcm", "zip"]} maxFiles={12} typeLabel="Carica solo file 3D: STL, DCM o ZIP." hint="Fino a 12 file · STL, DCM, ZIP o cartelle non compresse · max 100MB per file" />
                </div>
                <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                  <Label className="flex items-center gap-1.5"><FileSignature className="h-4 w-4" /> Nuova prescrizione / documenti</Label>
                  <FileDropzone files={updPrescr} setFiles={setUpdPrescr} disabled={updSubmitting} allowedExts={["jpg", "jpeg", "png", "webp", "gif", "heic", "heif", "pdf", "doc", "docx", "xml", "txt"]} typeLabel="Carica foto o documenti: JPEG, PNG, PDF, DOC." hint="Foto e documenti · max 100MB" />
                </div>
                <div className="space-y-2">
                  <Label>Nota per il laboratorio</Label>
                  <Textarea data-testid="client-update-note" value={updNote} onChange={(e) => setUpdNote(e.target.value)} rows={3} placeholder="Descrivi le modifiche richieste dopo la prova..." />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">Nuova data di scadenza <span className="text-red-500">*</span></Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" data-testid="client-update-deadline-trigger" className={`w-full justify-start font-normal ${!updDeadline ? "text-slate-400" : ""}`}>
                        <CalendarIcon className="h-4 w-4 mr-2 text-slate-400" />
                        {updDeadline ? format(updDeadline, "dd MMM yyyy", { locale: it }) : "Seleziona la nuova scadenza (obbligatoria)"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={updDeadline} onSelect={setUpdDeadline} disabled={(d) => isNonWorkingDay(d, options.closure_days || []) || d < new Date((updUrgency ? new Date() : updMinDate).setHours(0, 0, 0, 0))} initialFocus />
                    </PopoverContent>
                  </Popover>
                  {updUrgency
                    ? <p className="text-xs text-amber-700" data-testid="client-update-deadline-hint">Urgenza attiva: puoi scegliere qualsiasi giorno lavorativo a partire da oggi.</p>
                    : <p className="text-xs text-slate-500" data-testid="client-update-deadline-hint">Tempistiche: min. {updMinDays} gg lavorativi · prima data disponibile {format(updMinDate, "dd/MM/yyyy")}</p>}
                  {upcomingClosuresLabel(options.closure_days || []) && <p className="text-xs text-amber-700" data-testid="client-update-closures-hint">Laboratorio chiuso: {upcomingClosuresLabel(options.closure_days || [])} (date non selezionabili)</p>}
                </div>
                {options.urgency_enabled !== false && (
                  <div className={`rounded-lg border p-3 flex items-start gap-3 ${updUrgency ? "border-amber-300 bg-amber-100/60" : "border-slate-200 bg-white"}`} data-testid="client-update-urgency-banner">
                    <Zap className={`h-5 w-5 mt-0.5 shrink-0 ${updUrgency ? "text-amber-600" : "text-slate-400"}`} />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">Richiedi urgenza</p>
                      <p className="text-xs text-slate-500">Ti serve prima dei tempi minimi? Attiva l'urgenza per scegliere una data più ravvicinata.</p>
                    </div>
                    <Switch data-testid="client-update-urgency-toggle" checked={updUrgency} onCheckedChange={(v) => { setUpdUrgency(!!v); if (!v && updDeadline && updDeadline < updMinDate) setUpdDeadline(undefined); }} className="mt-0.5" />
                  </div>
                )}
                <Button data-testid="submit-client-update" onClick={submitClientUpdate} disabled={updSubmitting} className="bg-amber-600 hover:bg-amber-700">
                  {updSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Invia aggiornamento
                </Button>
                <UploadProgress progress={uploadProgress} />
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 border-slate-200">
            <h3 className="font-heading text-lg font-semibold mb-4">Cliente</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2.5 text-slate-700"><User className="h-4 w-4 text-slate-400" />{order.client_name}</div>
              {order.client_company && <div className="flex items-center gap-2.5 text-slate-700"><Building2 className="h-4 w-4 text-slate-400" />{order.client_company}</div>}
              <div className="flex items-center gap-2.5 text-slate-700"><Mail className="h-4 w-4 text-slate-400" />{order.client_email}</div>
            </div>
            {isAdmin && order.client_billing && (
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 text-sm text-slate-600" data-testid="client-billing">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Dati fatturazione</p>
                <p>P.IVA: {order.client_billing.vat_number || "—"}</p>
                <p>Cod. Fiscale: {order.client_billing.tax_code || "—"}</p>
                <p>{order.client_billing.address || "—"}</p>
                <p>{order.client_billing.zip_code} {order.client_billing.city} ({order.client_billing.province})</p>
                <p>SDI: {order.client_billing.sdi_code || "—"}</p>
                <p>PEC: {order.client_billing.pec || "—"}</p>
                {order.client_billing.phone && <p>Tel: {order.client_billing.phone}</p>}
              </div>
            )}
          </Card>

          {isStaff && (
            <Card className="p-6 border-slate-200" data-testid="admin-controls">
              <h3 className="font-heading text-lg font-semibold mb-4">Gestione stato</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Stato ordine</Label>
                  <Select value={order.status} onValueChange={updateStatus}>
                    <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nota per il cliente</Label>
                  <Textarea data-testid="admin-note-input" value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={3} placeholder="Aggiungi una nota..." />
                </div>
                <Button data-testid="save-admin-note-button" onClick={() => updateStatus(order.status)} className="w-full bg-teal-600 hover:bg-teal-700">Salva</Button>
              </div>
            </Card>
          )}

          {isAdmin && (
            <Card className="p-6 border-red-300 bg-red-50" data-testid="danger-zone">
              <h3 className="font-heading text-lg font-semibold mb-1 text-red-700">Zona pericolo</h3>
              <p className="text-sm text-red-700/80 mb-4">L'eliminazione dell'ordine è definitiva e rimuove anche i file associati.</p>
              <Button data-testid="delete-order-button" onClick={() => setOrderDeleteOpen(true)} className="w-full bg-red-600 hover:bg-red-700 text-white shadow-sm">
                <Trash2 className="h-4 w-4 mr-1.5" /> Elimina ordine
              </Button>
            </Card>
          )}
        </div>
      </div>

      <AlertDialog open={!!fileToDelete} onOpenChange={(o) => !o && setFileToDelete(null)}>
        <AlertDialogContent data-testid="delete-file-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare definitivamente il file <span className="font-semibold text-slate-800">{fileToDelete?.filename}</span> caricato dal cliente. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-file" disabled={deleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-file" onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />} Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={orderDeleteOpen} onOpenChange={(o) => !deletingOrder && setOrderDeleteOpen(o)}>
        <AlertDialogContent data-testid="delete-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sei sicuro?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare definitivamente l'ordine <span className="font-semibold text-slate-800">{order.order_number}</span> di <span className="font-semibold text-slate-800">{order.client_name}</span> e tutti i file associati. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-order" disabled={deletingOrder}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-order" onClick={(e) => { e.preventDefault(); confirmDeleteOrder(); }} disabled={deletingOrder} className="bg-red-600 hover:bg-red-700">
              {deletingOrder ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />} Elimina ordine
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="text-slate-800 mt-0.5">{value}</dd>
    </div>
  );
}
